<template>
  <div class="p-view narrow-page flex flex-col items-center">
    <SettingsBox v-if="hasPermission('manageUsers')">
      <InviteUsers />
    </SettingsBox>

    <SettingsBox v-if="canSeeApprovals">
      <Approvals
          :checklist-approvers="checklist.approvers"
          :users="approvalUsers"
          :show-description="true"
          :approvals-enabled.sync="checklist.approvalsEnabled"
          :entity-restrictions.sync="checklist.approvalsEntitySpecificAssignments"
          @update:checklistFormApprovers="updateChecklistFormApprovers"
          @update:updateApprovalsEnabled="updateApprovalsEnabled"
          @update:updateEntityRestrictions="updateEntityRestrictions"
      />
    </SettingsBox>

    <SettingsBox v-if="canSeeTranslations">
      <Translations
        :translation-languages="checklist.fullTranslationLanguages"
        :languages="languages"
        :contains-error="languageDetectionFailed"
        @update-translation-languages="onUpdateTranslationLanguages"
      />
    </SettingsBox>

    <SettingsBox>
      <Availability
        :restrictions="checklist.restrictions"
        :users="users"
        :groups="groups"
        :entity-items="entityItems"
        :roles="roles"
      />
    </SettingsBox>

    <SettingsBox>
      <Editors
        :checklist-editors="checklist.editors"
        :users="users"
        :groups="groups"
        :roles="roles"
      />
    </SettingsBox>

    <SettingsBox v-if="isReportViewRestrictionsEnabled">
      <ReportViewers
        :report-viewers="checklist.reportRestrictions"
        :users="users"
        :groups="groups"
        :roles="roles"
      />
    </SettingsBox>

    <SettingsBox>
      <p class="checklist-builder__second-stage-title">
        {{ $t('template-builder.schedule.title') }}
      </p>
      <p class="text-sm text-70 mb-4">
        {{ $t('template-builder.schedule.description') }}
      </p>
      <ChooseScheduleField
        :placeholder="$t('template-builder.schedule.select')"
        :value="checklist.schedules"
        :options="schedules"
        :select-all-enabled="true"
        :deselect-all-enabled="true"
        @input="onScheduleUpdate"
      />
    </SettingsBox>

    <SettingsBox>
      <actions />
    </SettingsBox>

    <SettingsBox>
      <ChecklistTemplates />
    </SettingsBox>

    <SettingsBox>
      <ReceiveReportByMail :users="users" :checklist-id="checklistId" />
    </SettingsBox>

    <title-configuration
      :value="submitted_form_title_settings"
      @input="onSubmittedFormTitleSettingsUpdate"
    />

    <SettingsBox>
      <qr-code :checklist-id="checklistId" />
    </SettingsBox>

    <SettingsBox v-if="isRoccScoringEnabled">
      <CategoryScoring />
    </SettingsBox>
    <SettingsBox v-if="isAiBlurFacesEnabled">
      <AiAnonymization
        :value="checklist.blurFaces"
        @input="onBlurFacesUpdate"
      />
    </SettingsBox>

    <SettingsBox v-if="isAiImproveWritingEnabled">
      <AiWritingAssistant
        :value="checklist.improveWriting"
        @input="onImproveWritingUpdate"
      />
    </SettingsBox>

  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Approvals from '@/_legacy/views/ChecklistBuilder/Settings/Approvals';
  import Availability from '@/_legacy/views/ChecklistBuilder/Settings/Availability';
  import ReportViewers from '@/_legacy/views/ChecklistBuilder/Settings/ReportViewers';
  import Editors from '@/_legacy/views/ChecklistBuilder/Settings/Editors';
  import InviteUsers from '@/_legacy/views/ChecklistBuilder/Settings/InviteUsers.vue';
  import { Actions } from '@/_legacy/views/ChecklistBuilder/Settings/Actions';
  import { ChecklistTemplates } from '@/_legacy/views/ChecklistBuilder/Settings/ChecklistTemplates';
  import { QrCode } from '@/_legacy/views/ChecklistBuilder/Settings/QrCode';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { ReceiveReportByMail } from '@/ui/components/checklist/assign/ReceiveReportByMail';
  import { TitleConfiguration } from '@/ui/components/checklist/TitleConfiguration';
  import ChooseScheduleField from '@/ui/components/schedule/ChooseScheduleField';
  import { useTask } from '@/ui/concerns';
  import { filter as scheduleFilter } from '@/services/api/resources/schedule';
  import { toasted } from '@/ui/helpers';
  import { t } from '@/ui/i18n/i18n';
  import emitter from '@/services/emitter';
  import {
    approvalsEnabledForOrganization,
    hasSomePermission,
    inspectionApprovalsPermissions,
    isFormTranslationsFeatureEnabled,
  } from '@/ui/auth/auth';
  import { isReportViewRestrictionsEnabled, isRoccScoringEnabled, isAiBlurFacesEnabled, isAiImproveWritingEnabled } from '@/ui/helpers/featureHelper';
  import Translations from '@/_legacy/views/ChecklistBuilder/Settings/Translations.vue';
  import SettingsBox from '@/_legacy/views/ChecklistBuilder/Settings/SettingsBox.vue';
  import CategoryScoring from '@/_legacy/views/ChecklistBuilder/Settings/CategoryScoring.vue';
  import AiAnonymization from '@/_legacy/views/ChecklistBuilder/Settings/AiAnonymization.vue';
  import AiWritingAssistant from '@/_legacy/views/ChecklistBuilder/Settings/AiWritingAssistant.vue';
  import { FormLanguageDetectionStatus } from '@/services/api/resources/checklist/constants';

  export default {
    components: {
      SettingsBox,
      Translations,
      CategoryScoring,
      InviteUsers,
      Availability,
      ReportViewers,
      Approvals,
      Editors,
      ReceiveReportByMail,
      Actions,
      ChecklistTemplates,
      QrCode,
      TitleConfiguration,
      ChooseScheduleField,
      AiAnonymization,
      AiWritingAssistant,
    },

    mixins: [authMixin],

    props: {
      isUpdate: {
        type: Boolean,
        default: false,
      },
      resourceId: {
        type: String,
        default: '',
      },
    },

    data: () => ({
      title: 'Assign',
    }),

    computed: {
      ...mapGetters({
        checklist: 'checklist_builder/checklist',
        stageSettingsSetting: 'checklist_builder/stageSettingsSetting',
        groups: 'checklist_builder/groups',
        entityItems: 'checklist_builder/entityItems',
        users: 'checklist_builder/users',
        approvalUsers: 'checklist_builder/approvalUsers',
        schedules: 'checklist_builder/schedules',
        roles: 'checklist_builder/roles',
        languages: 'checklist_builder/languages',
        submitted_form_title_settings: 'checklist_builder/submitted_form_title_settings',
        can_see_all_entity_items: 'checklist_builder/can_see_all_entity_items',
      }),

      canSeeApprovals() {
        return approvalsEnabledForOrganization.value
          && hasSomePermission(inspectionApprovalsPermissions);
      },

      canSeeTranslations() {
        return isFormTranslationsFeatureEnabled.value;
      },

      checklistId() {
        return this.resourceId !== '' ? this.resourceId : null;
      },

      isReportViewRestrictionsEnabled,

      isAiBlurFacesEnabled,

      isAiImproveWritingEnabled,

      languageDetectionFailed() {
        return ![null, FormLanguageDetectionStatus.detected].includes(this.checklist.locale_detection_status);
      },

      isRoccScoringEnabled,
    },

    destroyed() {
      emitter.off('scheduleCreated', this.onScheduleCreated);
    },

    created() {
      emitter.on('scheduleCreated', this.onScheduleCreated);
      this.fetchSchedules();
    },

    methods: {
      async fetchSchedules() {
        const getSchedules = useTask(
          () => scheduleFilter(),
          payload => this.$store.commit('checklist_builder/loadSchedules', payload.data),
        );
        return getSchedules.run();
      },
      updateApprovalsEnabled(newValue) {
        this.$store.commit('checklist_builder/setApprovalsEnabled', newValue);
      },
      updateEntityRestrictions(newValue) {
        this.$store.commit('checklist_builder/setApprovalsEntitySpecificAssignments', newValue);
      },
      updateChecklistFormApprovers(newValue) {
        this.$store.commit('checklist_builder/setApprovers', newValue);
      },
      async onScheduleCreated({ response }) {
        toasted.success(t('schedules.created'), { duration: 5000 });

        await this.fetchSchedules();

        const schedules = [...this.checklist.schedules, response.id];

        this.$store.commit('checklist_builder/setSchedules', schedules);
      },
      onSubmittedFormTitleSettingsUpdate(value) {
        this.$store.commit('checklist_builder/setSubmittedFormTitleSettings', value);
      },

      onScheduleUpdate(value) {
        this.$store.commit('checklist_builder/setSchedules', value);
      },

      onUpdateTranslationLanguages(langs) {
        this.$store.dispatch('checklist_builder/setTranslationLanguages', langs);
      },

      onBlurFacesUpdate(value) {
        this.$store.commit('checklist_builder/setBlurFaces', value);
      },

      onImproveWritingUpdate(value) {
        this.$store.commit('checklist_builder/setImproveWriting', value);
      },
    },
  };
</script>
